import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Badge, Button, Checkbox, Empty, Popover } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { actions as circleFilterActions, selectors as circleFilterSelectors } from 'redux/circleFilter';
import { CIRCLE_FILTER_STORAGE_KEY, parsePersistedCircleIds } from 'lib/circleFilterStorage';

// BFC-9035: Global Circle Filter (Control Tower only). An empty selection means "All Circles".
function CircleFilter({ size = 'medium' }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const circles = useSelector(circleFilterSelectors.selectCircles);
  const selectedCircleIds = useSelector(circleFilterSelectors.selectSelectedCircleIds);
  const [visible, setVisible] = useState(false);
  const [draftIds, setDraftIds] = useState(selectedCircleIds);

  useEffect(() => {
    if (!circles.length) {
      dispatch(circleFilterActions.fetchCircles());
    }
  }, []);

  // Drop saved selections for circles no longer offered (e.g. unmapped circles) - they can't be unticked otherwise
  useEffect(() => {
    if (!circles.length || !selectedCircleIds.length) return;
    const validIds = selectedCircleIds.filter((id) => circles.some((circle) => circle.biofuelcircle_master_id === id));
    if (validIds.length !== selectedCircleIds.length) {
      dispatch(circleFilterActions.setSelectedCircleIds(validIds));
    }
  }, [circles]);

  // Keep other open tabs in sync - the selection is persisted in localStorage
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CIRCLE_FILTER_STORAGE_KEY) {
        dispatch(circleFilterActions.setSelectedCircleIds(parsePersistedCircleIds(e.newValue)));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const allIds = circles.map((circle) => circle.biofuelcircle_master_id);

  // The draft is the explicit list of ticked circles; the saved "All Circles" ([]) opens with every circle ticked
  useEffect(() => {
    if (visible) {
      setDraftIds(selectedCircleIds.length ? selectedCircleIds : allIds);
    }
  }, [visible]);

  const isAllSelected = allIds.length > 0 && draftIds.length === allIds.length;
  const isPartlySelected = draftIds.length > 0 && !isAllSelected;

  const applySelection = () => {
    // Every circle ticked is saved as "All Circles" so no circle filter is sent
    const ids = isAllSelected ? [] : draftIds;
    dispatch(circleFilterActions.setSelectedCircleIds(ids));
    setVisible(false);

    // Go back to the first page so a narrower result set is not left on an empty page
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('page') && searchParams.get('page') !== '1') {
      searchParams.set('page', '1');
      history.push({ pathname: location.pathname, search: searchParams.toString() });
    }
  };

  const content = (
    <div style={{ width: 240 }}>
      {circles.length ? (
        <>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isPartlySelected}
            onChange={(e) => setDraftIds(e.target.checked ? allIds : [])}>
            All Circles
          </Checkbox>
          <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: 8, paddingLeft: 8 }}>
            <Checkbox.Group
              value={draftIds}
              onChange={(values) => setDraftIds(values)}
              style={{ display: 'flex', flexDirection: 'column' }}>
              {circles.map((circle) => (
                <Checkbox
                  key={circle.biofuelcircle_master_id}
                  value={circle.biofuelcircle_master_id}
                  style={{ marginLeft: 0, marginBottom: 4 }}>
                  {circle.circle_name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
          <div className="d-flex justify-content-end mt-10">
            <Button size="small" className="mr-10" onClick={() => setVisible(false)}>
              Cancel
            </Button>
            <Button
              size="small"
              type="primary"
              disabled={!draftIds.length}
              title={!draftIds.length ? 'Select at least one circle' : undefined}
              onClick={applySelection}>
              Apply
            </Button>
          </div>
        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No circles found" />
      )}
    </div>
  );

  // `circle-filter-btn` overrides the global `.anticon { display: block !important }` (antTable.scss)
  // so the icon stays inline; the button keeps antd's default layout to line up with the footer buttons.
  return (
    <Popover
      content={content}
      title="Filter by Circle"
      trigger="click"
      placement="topRight"
      visible={visible}
      onVisibleChange={setVisible}>
      <Badge count={selectedCircleIds.length} size="small" offset={[-4, 2]} className="report-btn">
        <Button
          size={size}
          className="paganation-btn filter-btn circle-filter-btn"
          title={selectedCircleIds.length ? `${selectedCircleIds.length} Circle(s) selected` : 'All Circles'}>
          <EnvironmentOutlined />
          Circle
        </Button>
      </Badge>
    </Popover>
  );
}

export default CircleFilter;
